import React from "react";

import { FormButton } from "components";
import { FormikTextInput } from "components/UI/Inputs/FormikInputs";

import { Form, Formik } from "formik";

import MapaPreview from "components/utils/mapa/MapaPreview";
import {
    ANCHOS_DOMICILIO,
    ANCHOS_DOMICILIO_ANGOSTO,
    clasesDeColumnas,
    estilos,
} from "components/utils/mapa/BloqueUbicacion";

/**
 * Vista de solo lectura de un establecimiento, compartida por las cinco entidades.
 *
 * Es el espejo de EstablecimientoFormulario: mismo layout de dos columnas, mismos campos en el
 * mismo orden y el mismo mapa, pero sin nada que se pueda tocar. Va como componente aparte y
 * no como un modo de BloqueUbicacion porque de ese bloque, en la vista, no queda casi nada en
 * pie: no hay busqueda de la direccion, no hay leyenda de estado, el pin no se arrastra y la
 * localidad no es un select sino el nombre que ya trae el registro. Un `soloLectura` que
 * apagara todo eso seria un if por linea.
 *
 * Lo que si comparte es lo que importa que no se desincronice: las clases de la grilla, los
 * anchos de los campos y el componente del mapa.
 */

/**
 * El campo de la vista: el mismo input de siempre, sin permitir edicion.
 *
 * Conserva la linea reservada del helper aunque aca no haya ningun error que mostrar. Se puede
 * apagar con helperText="", pero entonces cada fila mide ~20px menos que en el formulario, la
 * columna de carga termina antes que el mapa y latitud y longitud vuelven a quedar colgando
 * por debajo del ultimo campo. Que las dos pantallas midan igual es lo que las mantiene
 * alineadas.
 */
function Campo({ className, name, labelText }) {
    return (
        <FormikTextInput
            className={className}
            name={name}
            labelText={labelText}
            readOnly
        />
    );
}

/**
 * Nombre y codigo, en el mismo orden y con los mismos anchos que en el formulario.
 */
export function CamposIdentificacionVista({
    anchoName = estilos.col8,
    anchoCod = estilos.col4,
}) {
    return (
        <>
            <Campo className={anchoName} name="name" labelText="Nombre" />
            <Campo className={anchoCod} name="cod" labelText="Código" />
        </>
    );
}

/**
 * @param valores     los datos del registro, ya resueltos a nombres: la localidad, el nivel y
 *                    la zona se muestran como texto, no como ids.
 * @param camposExtra campos propios de la entidad, al principio de la grilla. Si no se pasa,
 *                    van nombre y codigo solos.
 * @param columnas    anchos de los campos del domicilio, si la entidad necesita otros.
 * @param mapaAncho   reparto 40/60 y campos de a uno por fila. Sigue al formulario: la ficha
 *                    y la edicion del mismo registro tienen que verse igual.
 */
export default function EstablecimientoVista({
    valores,
    closeModal,
    camposExtra = null,
    columnas = null,
    mapaAncho = true,
}) {
    const col = clasesDeColumnas(
        columnas ?? (mapaAncho ? ANCHOS_DOMICILIO_ANGOSTO : ANCHOS_DOMICILIO)
    );

    return (
        <Formik initialValues={valores} onSubmit={() => {}}>
            <Form noValidate>
                <div
                    className={`${estilos.cuerpo} ${mapaAncho ? estilos.cuerpoMapaAncho : ""}`}
                >
                    <div className={estilos.columna}>
                        <section className={estilos.section}>
                            <div className={estilos.grid}>
                                {camposExtra ?? <CamposIdentificacionVista />}
                                <Campo
                                    className={col.calle}
                                    name="calle"
                                    labelText="Calle"
                                />
                                <Campo
                                    className={col.numero}
                                    name="numero"
                                    labelText="Número"
                                />
                                <Campo
                                    className={col.localidad}
                                    name="localidad"
                                    labelText="Localidad"
                                />
                                <Campo
                                    className={col.cpa}
                                    name="cpa"
                                    labelText="Código postal (CPA)"
                                />
                            </div>
                        </section>
                    </div>

                    <section
                        className={`${estilos.section} ${estilos.conTitulo} ${estilos.geoSection}`}
                    >
                        <h3 className={estilos.sectionTitle}>Geolocalización</h3>
                        <div className={estilos.mapPanel}>
                            <div className={estilos.mapCard}>
                                <div className={estilos.mapContainer}>
                                    {/* Sin onArrastrarPin el pin queda fijo: se puede pasear
                                        por el mapa y hacer zoom, pero no mover la marca. */}
                                    <MapaPreview
                                        lat={valores.lat}
                                        lng={valores.lng}
                                    />
                                </div>
                            </div>
                            <div
                                className={`${estilos.grid} ${estilos.coordenadas}`}
                            >
                                <FormikTextInput
                                    className={estilos.col6}
                                    name="lat"
                                    labelText="Latitud"
                                    disabled={true}
                                />
                                <FormikTextInput
                                    className={estilos.col6}
                                    name="lng"
                                    labelText="Longitud"
                                    disabled={true}
                                />
                            </div>
                        </div>
                    </section>
                </div>

                <FormButton
                    oneButton={true}
                    closeButtonText="Cerrar"
                    onCloseModal={closeModal}
                />
            </Form>
        </Formik>
    );
}
